/*
 *                     NOTICE
 *            COPYRIGHT 2025 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbButton } from "abreact/misc/AbButton";
import { AbImage } from "abreact/misc/AbImage";
import { AbItem } from "abreact/misc/AbItem";
import { AbSize } from "abreact/misc/AbSize";
import React, { useCallback, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
/**
 * Represents a single image in the IconGrid
 */
const IconGridCell = ({
  image,
  className
}) => {
  const imageId = image.fullId;
  const sdkInfo = image.extra;
  const label = sdkInfo?.semanticName || imageId;
  return /*#__PURE__*/React.createElement("div", {
    className: ReactUtils.classNamesString(["icon-grid-cell", className])
  }, /*#__PURE__*/React.createElement("div", {
    className: "icon-image"
  }, /*#__PURE__*/React.createElement(AbImage, {
    image: imageId,
    tooltip: imageId,
    width: 32,
    height: 32
  })), /*#__PURE__*/React.createElement(AbItem, {
    className: "icon-label",
    label: label
  }));
};
/**
 * Renders a grid of IconGridCells
 * Meant to be used in IconSearchPicker
 */
export const IconGrid = ({
  images = [],
  onImageClick,
  className = "",
  width = 800,
  height = 600,
  selectedImageId
}) => {
  // dimensions of gridCell that will have to be updated if css changes
  const CELL_WIDTH = 135;
  const CELL_HEIGHT = 118;

  // Calculate number of images that fit in one row
  const imagesPerRow = Math.max(1, Math.floor(width / (CELL_WIDTH + 4)));
  const rowCount = Math.ceil(images.length / imagesPerRow);

  // to indicate the scroll container to tanstack useVirtualizer
  const parentRef = useRef(null);
  const getItemKey = useCallback(index => {
    const image = images[index]; // Access directly using index
    return image ? image.fullId : ''; // Return fullId as key
  }, [images]);
  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => CELL_HEIGHT,
    overscan: 5,
    getItemKey: getItemKey,
    useAnimationFrameWithResizeObserver: true,
    gap: 4
  });
  return /*#__PURE__*/React.createElement("div", {
    ref: parentRef,
    "data-test-id": "icon-grid",
    className: `icon-grid ${className}`,
    style: {
      width,
      height
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      height: virtualizer.getTotalSize(),
      position: 'relative'
    }
  }, virtualizer.getVirtualItems().map(({
    index,
    start
  }) => {
    const fromIndex = index * imagesPerRow;
    const toIndex = Math.min(fromIndex + imagesPerRow, images.length);
    return /*#__PURE__*/React.createElement("div", {
      key: index,
      className: "icon-grid-row",
      style: {
        top: start
      }
    }, images.slice(fromIndex, toIndex).map(image => {
      const id = image.fullId;
      return /*#__PURE__*/React.createElement(AbButton, {
        key: id,
        onClick: () => onImageClick?.(image),
        style: {
          marginLeft: 4,
          marginRight: 4
        },
        size: AbSize.CUSTOM,
        dataTestId: `icon-grid-button-${id}`,
        tooltip: image.fullId,
        label: /*#__PURE__*/React.createElement(IconGridCell, {
          className: selectedImageId === id ? "selected" : undefined,
          image: image
        })
      });
    }));
  })));
};
//# sourceMappingURL=IconGrid.js.map
